import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ContaBancoProps, SelectType } from './protocols';
import { InputContainer } from './styles';
import { Form, Spinner } from 'react-bootstrap';
import { useInfiniteQuery, useQueryClient } from 'react-query';
import { Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import api from '~/services/api';
import useDebounce from '~/hooks/useDebounce';
import { Lista } from './Lista';

/**
 * @param {ContaBancoProps} ContaBanco
 */
export const ContaBanco: React.FC<ContaBancoProps> = ({
  label = '',
  placeholder = '',
  name,
  register,
  isError,
  control,
  changeSelected,
  loja,
  retriever = null,
  ListaWidth = '100%',
  allowNone,
  noneOptionLabel,
  ...rest
}: ContaBancoProps) => {
  const { debouncedFn } = useDebounce();
  const inputRef: any = useRef(null);

  const [selected, setSelected] = useState<SelectType>({
    label: undefined as unknown as string,
    value: undefined as unknown as number,
  });

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>('');
  const limit = 10;

  const queryClient = useQueryClient();

  const filterContas = useCallback(
    (options: any[]): any[] => {
      const filteredOptions = options.filter((item) =>
        String(item.descricao)
          .toLowerCase()
          .includes(String(filter).toLowerCase()),
      );
      return filteredOptions;
    },
    [filter],
  );

  /**
   * Normaliza `loja` para sempre lidarmos com array internamente.
   */
  const lojasArray: number[] = React.useMemo(() => {
    if (!loja) return [];
    return Array.isArray(loja) ? loja : [loja];
  }, [loja]);

  const hasMultipleLojas = lojasArray.length > 1;

  const fetchData = useCallback(
    async ({ pageParam = 1 }): Promise<any> => {
      try {
        // Sem loja informada: retorna vazio
        if (!lojasArray.length) {
          return {
            count: '0',
            data: [],
            fields: [],
            pagination: { lastPage: 0, prevPage: 1, startPage: 1 },
            success: true,
          };
        }

        // Cache apenas quando for uma única loja (mantém comportamento atual)
        if (!hasMultipleLojas) {
          const lojaUnica = lojasArray[0];
          const cacheKey = `cache_bancos_${lojaUnica}`;
          const cache_bancos: any = queryClient.getQueryData(cacheKey);

          if (cache_bancos && cache_bancos.loja === lojaUnica) {
            if (retriever && !isOpen) {
              const options = cache_bancos.data ? cache_bancos.data : [];
              const selectedRetriever = options.filter(
                (d: any) => d.value === retriever,
              );
              changeSelected({
                value: selectedRetriever[0] ? selectedRetriever[0].value : null,
                label: selectedRetriever[0]
                  ? selectedRetriever[0].label
                  : 'Selecione',
                lojaId: lojaUnica,
              });
            }

            return { ...cache_bancos, data: filterContas(cache_bancos.data) };
          }

          const { data: dataFields } = await api.get(
            `/contas/loja/${lojaUnica}`,
            {
              params: { page: pageParam, limit, filter },
            },
          );

          const options = filterContas(dataFields.data)
            .filter((d: any) => d.flg_inativo !== true)
            .map((dt: any) => ({
              value: dt.cod_cc,
              label: `${dt.descricao}`,
              lojaId: lojaUnica,
              ...dt,
            }));

          queryClient.setQueryData(cacheKey, {
            ...dataFields,
            data: options,
            loja: lojaUnica,
          });

          if (retriever && !isOpen) {
            const selectedRetriever = options.filter(
              (d: any) => d.value === retriever,
            );
            changeSelected({
              value: selectedRetriever[0] ? selectedRetriever[0].value : null,
              label: selectedRetriever[0]
                ? selectedRetriever[0].label
                : 'Selecione',
              lojaId: lojaUnica,
            });
          }

          return { ...dataFields, data: options };
        }

        // MÚLTIPLAS LOJAS
        // Busca paralela por loja e mescla resultados, etiquetando por lojaId.
        const responses = await Promise.all(
          lojasArray.map(async (lj) => {
            const { data: dataFields } = await api.get(`/contas/loja/${lj}`, {
              params: { page: pageParam, limit, filter },
            });

            const options = filterContas(dataFields.data)
              .filter((d: any) => d.flg_inativo !== true)
              .map((dt: any) => ({
                value: dt.cod_cc,
                label: `${dt.descricao}`,
                lojaId: lj,
                ...dt,
              }));

            return { lojaId: lj, data: options, meta: dataFields };
          }),
        );

        // Mescla os itens numa única estrutura compatível com a Lista
        const merged = responses.flatMap((r) => r.data);

        // Em caso de multiple lojas, não tentamos "retriever" automático para evitar ambiguidade.

        return {
          count: String(merged.length),
          data: merged, // cada item tem lojaId
          fields: [],
          pagination: {
            lastPage: Math.max(
              ...responses.map((r) => r.meta?.pagination?.lastPage || 1),
            ),
            prevPage: pageParam > 1 ? pageParam - 1 : 1,
            startPage: 1,
          },
          success: true,
        };
      } catch (error: any) {
        if (error?.data?.message) {
          toast.error(error.data.message);
        } else {
          toast.error(String(error));
        }
        return {
          count: '0',
          data: [],
          fields: [],
          pagination: { lastPage: 0, prevPage: 1, startPage: 1 },
          success: true,
        };
      }
    },
    [
      isOpen,
      lojasArray,
      hasMultipleLojas,
      filter,
      filterContas,
      queryClient,
      retriever,
      changeSelected,
      limit,
    ],
  );

  const { data, hasNextPage, fetchNextPage, isFetching, refetch } =
    useInfiniteQuery(
      // a chave de query inclui as lojas pra invalidar corretamente
      [name, JSON.stringify(lojasArray), filter],
      ({ pageParam = 1 }) => fetchData({ pageParam }),
      { refetchOnWindowFocus: false },
    );

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(lojasArray)]);

  return (
    <InputContainer>
      <Form.Group>
        <Form.Label>{label}</Form.Label>
        <Controller
          name={name}
          control={control}
          defaultValue=""
          render={({ field: { value } }) => {
            if (inputRef && inputRef.current) {
              if (value) {
                if (
                  value.label !== selected.label &&
                  inputRef.current.placeholder !== placeholder
                ) {
                  inputRef.current.placeholder = placeholder;
                }
                if (value.label) {
                  inputRef.current.placeholder = `${value.label}`;
                }
                if (!value.label) {
                  inputRef.current.placeholder = placeholder;
                }
              } else {
                inputRef.current.placeholder = placeholder;
              }
            }
            return (
              <>
                <div className="select-container">
                  <label>
                    <input
                      type="text"
                      className={
                        isError ? 'form-control is-invalid' : 'form-control'
                      }
                      placeholder={placeholder}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        event.target.value = event.target.value.toUpperCase();
                        setFilter(event.target.value.toUpperCase());
                        if (event.target.value === '') {
                          queryClient.removeQueries([
                            name,
                            JSON.stringify(lojasArray),
                          ]);
                        }
                        debouncedFn(() => refetch(), 500);
                      }}
                      autoComplete="off"
                      onClick={(event: any) => {
                        if (selected.label) {
                          event.target.placeholder = selected.label;
                        }
                        event.target.value = '';
                        setIsOpen(true);
                      }}
                      ref={inputRef}
                      {...rest}
                    />
                    <div className="drop-indicator">
                      <span role="img" aria-label="open">
                        {filter && isFetching ? (
                          <Spinner
                            animation="border"
                            size="sm"
                            className="spinner"
                          />
                        ) : (
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            role="presentation"
                          >
                            <path
                              d="M8.292 10.293a1.009 1.009 0 000 1.419l2.939 2.965c.218.215.5.322.779.322s.556-.107.769-.322l2.93-2.955a1.01 1.01 0 000-1.419.987.987 0 00-1.406 0l-2.298 2.317-2.307-2.327a.99.99 0 00-1.406 0z"
                              fill="currentColor"
                              fillRule="evenodd"
                            />
                          </svg>
                        )}
                      </span>
                    </div>
                  </label>
                </div>
                {isOpen && (
                  <Lista
                    listaWidth={ListaWidth}
                    inputRef={inputRef}
                    setIsOpen={setIsOpen}
                    setFilter={setFilter}
                    hasNextPage={hasNextPage as boolean}
                    fetchNextPage={fetchNextPage}
                    data={data}
                    selected={selected}
                    setSelected={setSelected}
                    isFetching={isFetching}
                    queryName={name}
                    changeSelected={changeSelected}
                    queryClient={queryClient}
                    allowNone={allowNone}
                    noneOptionLabel={noneOptionLabel}
                  />
                )}
              </>
            );
          }}
        />
      </Form.Group>
    </InputContainer>
  );
};
